import React, { useEffect, useRef, useState } from 'react'
import { Banner } from './Banner'
import { Button } from './Button'
import { Spinner } from './Spinner'
import { useSpeedbump } from './Speedbump'
import { useVodUpload, VOD_UPLOAD_FAILURE_MESSAGES } from '../hooks'
import { fetchVodMasterPreview } from '../services/vodUpload'
import { versionFromKey } from '../utils/vodVersion'
import type { VodRendition } from '../types/vod'
import styles from './VodUploadPanel.module.scss'

interface VodUploadPanelProps {
  assetId: string
  /** The asset's catalogued SOURCE master, if any (renditions[] kind SOURCE) */
  master?: VodRendition | null
  /** A playable rendition exists — packaging already ran */
  isPackaged?: boolean
  /** Master version this page rendered (0 = no master) — the open-upload
   *  endpoint 412s when the server's current version differs */
  expectedMasterVersion: number
  onUploadCompleted: () => void
}

const formatBytes = (bytes: number): string => {
  if (!bytes) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  const value = bytes / 1024 ** exponent
  return `${value >= 100 ? Math.round(value) : value.toFixed(1)} ${units[exponent]}`
}

const describeMaster = (master: VodRendition, isPackaged: boolean): string => {
  const parts: string[] = []
  const version = versionFromKey(master.s3_key)
  if (version !== null) parts.push(`v${version}`)
  if (master.width && master.height) parts.push(`${master.width}×${master.height}`)
  if (master.content_length) parts.push(formatBytes(master.content_length))
  parts.push(isPackaged ? 'packaged' : 'awaiting packaging')
  return `Master uploaded — ${parts.join(' · ')}`
}

interface MasterPreviewProps {
  assetId: string
}

/**
 * Inline player for the master via an authenticated, expiring presigned GET
 * (editor preview only — never a delivery URL). preload="metadata" keeps page
 * load light; bytes stream only on play. A playback error re-fetches the URL
 * once, covering a link that expired while the page sat open. Mount it with
 * key={master.s3_key} so a replaced master remounts and fetches a fresh URL.
 */
const MasterPreview: React.FC<MasterPreviewProps> = ({ assetId }) => {
  const [url, setUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const retriedRef = useRef(false)

  useEffect(() => {
    let cancelled = false
    fetchVodMasterPreview(assetId)
      .then((preview) => {
        if (!cancelled) setUrl(preview.url)
      })
      .catch((err) => {
        if (!cancelled) setError((err as Error).message || 'Failed to fetch the preview URL')
      })
    return () => {
      cancelled = true
    }
  }, [assetId])

  const handleVideoError = () => {
    if (retriedRef.current) {
      setError('Playback failed — the preview link may have expired. Reload the page to retry.')
      return
    }
    retriedRef.current = true
    fetchVodMasterPreview(assetId)
      .then((preview) => setUrl(preview.url))
      .catch((err) => setError((err as Error).message || 'Failed to refresh the preview URL'))
  }

  if (error) return <Banner variant="error" message={error} />
  if (!url) return <Spinner size="small" message="Fetching preview…" />
  return (
    <video
      key={url}
      controls
      preload="metadata"
      src={url}
      className={styles.previewVideo}
      onError={handleVideoError}
    />
  )
}

/**
 * First-party master upload: file picker → presigned multipart (Uppy) →
 * synchronous validate at finalize. Handles pause/resume, cancel, verified
 * resume after a reload, and the self-healing finalize retry loop.
 */
export const VodUploadPanel: React.FC<VodUploadPanelProps> = ({
  assetId,
  master = null,
  isPackaged = false,
  expectedMasterVersion,
  onUploadCompleted,
}) => {
  const confirm = useSpeedbump()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const {
    phase,
    session,
    bytesUploaded,
    bytesTotal,
    isPaused,
    warnings,
    errorMessage,
    verifyProgress,
    isOpening,
    selectFile,
    pauseResume,
    cancel,
    startOver,
    reset,
  } = useVodUpload(assetId, onUploadCompleted, expectedMasterVersion)

  const handleFilePicked = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (file) selectFile(file)
  }

  const openFilePicker = () => fileInputRef.current?.click()

  const handleCancel = async () => {
    const ok = await confirm({
      title: 'Cancel this upload?',
      message: 'Uploaded parts are discarded and the upload starts over from scratch next time.',
      confirmLabel: 'Cancel upload',
      cancelLabel: 'Keep uploading',
      confirmVariant: 'danger',
    })
    if (ok) await cancel()
  }

  const handleStartOver = async () => {
    const ok = await confirm({
      title: 'Discard the interrupted upload?',
      message: 'The parts already uploaded are discarded and the selected file is uploaded from scratch.',
      confirmLabel: 'Start over',
      cancelLabel: 'Back',
      confirmVariant: 'danger',
    })
    if (ok) await startOver()
  }

  const progressPct = bytesTotal > 0 ? Math.min(100, (bytesUploaded / bytesTotal) * 100) : 0
  const failureMessage = session?.failure_code
    ? (VOD_UPLOAD_FAILURE_MESSAGES[session.failure_code] ?? session.failure_code)
    : 'The upload failed validation.'

  return (
    <div className={styles.uploadPanel}>
      <input
        ref={fileInputRef}
        type="file"
        accept="video/mp4,.mp4"
        onChange={handleFilePicked}
        className={styles.hiddenInput}
      />

      {errorMessage && phase !== 'mismatch' && (
        <Banner variant="error" message={errorMessage} />
      )}

      {phase === 'loading' && <Spinner size="small" message="Checking upload status…" />}

      {phase === 'idle' && (
        <div className={styles.pickerState}>
          {master && <Banner variant="success" message={describeMaster(master, isPackaged)} />}
          {master && (
            <p className={styles.masterKey}>
              <code>{master.s3_key}</code>
            </p>
          )}
          {master && <MasterPreview key={master.s3_key} assetId={assetId} />}
          <p className={styles.hint}>
            {master
              ? 'Upload a new master to replace the current one (a new version is created; the old master is retained until swept).'
              : 'Upload the master video for this asset. MP4 only, up to 20 GB — uploading does not make the asset playable; packaging follows separately.'}
          </p>
          <Button onClick={openFilePicker} disabled={isOpening}>
            {master ? 'Replace Video' : 'Upload Video'}
          </Button>
        </div>
      )}

      {phase === 'resume-prompt' && session && (
        <div className={styles.pickerState}>
          <Banner
            variant="info"
            message={
              <>
                An interrupted upload of{' '}
                <strong>{session.original_filename || 'a file'}</strong> (
                {formatBytes(session.declared_size_bytes)}) can be resumed. Re-select the
                same file — it is verified against the uploaded parts before resuming.
              </>
            }
          />
          <div className={styles.buttonRow}>
            <Button onClick={openFilePicker} disabled={isOpening}>
              Re-select File to Resume
            </Button>
            <Button variant="secondary" onClick={handleStartOver}>
              Discard &amp; Start Over
            </Button>
          </div>
        </div>
      )}

      {phase === 'active-elsewhere' && (
        <div className={styles.pickerState}>
          <Banner
            variant="info"
            message={
              <>
                This video is currently being uploaded in another tab
                {session?.original_filename ? (
                  <>
                    {' '}
                    (<strong>{session.original_filename}</strong>)
                  </>
                ) : null}
                .
              </>
            }
          />
        </div>
      )}

      {phase === 'verifying' && (
        <div className={styles.centeredState}>
          <Spinner
            size="small"
            message={
              verifyProgress && verifyProgress.total > 0
                ? `Verifying selected file against uploaded parts (${verifyProgress.verified}/${verifyProgress.total})…`
                : 'Verifying selected file…'
            }
          />
        </div>
      )}

      {phase === 'mismatch' && (
        <div className={styles.pickerState}>
          <Banner
            variant="error"
            message={
              errorMessage ||
              'The selected file does not match the interrupted upload.'
            }
          />
          <p className={styles.hint}>
            Resuming would splice two different files together, so it is blocked. Start
            over with the selected file, or re-select the original export.
          </p>
          <div className={styles.buttonRow}>
            <Button onClick={handleStartOver}>Start Over with Selected File</Button>
            <Button variant="secondary" onClick={openFilePicker} disabled={isOpening}>
              Choose a Different File
            </Button>
          </div>
        </div>
      )}

      {phase === 'uploading' && (
        <div className={styles.uploadingState}>
          <div className={styles.progressHeader}>
            <span>
              {isPaused ? 'Paused' : 'Uploading'}
              {session?.original_filename ? ` — ${session.original_filename}` : ''}
            </span>
            <span className={styles.progressNumbers}>
              {formatBytes(bytesUploaded)} / {formatBytes(bytesTotal)} (
              {progressPct.toFixed(0)}%)
            </span>
          </div>
          <div
            className={styles.progressTrack}
            role="progressbar"
            aria-valuenow={Math.round(progressPct)}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div className={styles.progressFill} style={{ width: `${progressPct}%` }} />
          </div>
          <div className={styles.buttonRow}>
            <Button variant="secondary" size="small" onClick={pauseResume}>
              {isPaused ? 'Resume' : 'Pause'}
            </Button>
            <Button variant="danger" size="small" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {phase === 'validating' && (
        <div className={styles.centeredState}>
          <Spinner size="small" message="Validating…" />
          <p className={styles.hint}>
            Assembling and validating the master — usually seconds. Interrupted
            validation retries automatically; leaving this page is safe.
          </p>
        </div>
      )}

      {phase === 'completed' && (
        <div className={styles.pickerState}>
          <Banner
            variant="success"
            message={`Master uploaded and validated${session ? ` (v${session.serving_version})` : ''}. The asset stays hidden until packaged and published.`}
          />
          {warnings.map((warning) => (
            <Banner
              key={warning}
              variant="warning"
              message={
                warning.startsWith('duration')
                  ? 'The video duration could not be read from the file — the asset will carry no duration label until a replacement master re-probes it (or packaging backfills it).'
                  : warning
              }
            />
          ))}
          {master && <MasterPreview key={master.s3_key} assetId={assetId} />}
          <div className={styles.buttonRow}>
            <Button variant="secondary" onClick={reset}>
              Replace Video
            </Button>
          </div>
        </div>
      )}

      {phase === 'failed' && (
        <div className={styles.pickerState}>
          <Banner variant="error" message={failureMessage} />
          {session?.error && (
            <p className={styles.masterKey}>
              <code>{session.error}</code>
            </p>
          )}
          <div className={styles.buttonRow}>
            <Button onClick={openFilePicker} disabled={isOpening}>
              Upload Again
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
